/**
 * Consent management (GDPR / ePrivacy).
 *
 * Stores the visitor's choice in localStorage under a versioned key so that
 * changing the policy later can re-prompt everyone. No cookie is set until the
 * visitor actively opts in to a category.
 */

export const CONSENT_VERSION = 1;
export const CONSENT_KEY = "cc_consent_v1";

export const CATEGORIES = {
  necessary: {
    id: "necessary",
    title: "Strictly necessary",
    description:
      "Required for the site to work (remembering your cookie choice, security). These cannot be switched off.",
    required: true,
  },
  analytics: {
    id: "analytics",
    title: "Analytics",
    description:
      "Google Analytics 4 — anonymous statistics about how the site is used, so we can improve it. Sets cookies (_ga, _ga_*).",
    required: false,
  },
  external: {
    id: "external",
    title: "External content",
    description:
      "Google Maps embeds. Loading a map sends your IP address and device data to Google in the USA.",
    required: false,
  },
};

export const DEFAULT_CONSENT = {
  necessary: true,
  analytics: false,
  external: false,
};

export function readConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return { ...DEFAULT_CONSENT, ...parsed.categories };
  } catch {
    return null;
  }
}

export function writeConsent(categories) {
  if (typeof window === "undefined") return;
  const payload = {
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
    categories: { ...DEFAULT_CONSENT, ...categories, necessary: true },
  };
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
  } catch {
    /* storage may be blocked — consent simply won't persist */
  }
  window.dispatchEvent(new CustomEvent("cc:consent", { detail: payload.categories }));
  return payload.categories;
}

export function clearConsent() {
  try {
    window.localStorage.removeItem(CONSENT_KEY);
  } catch {
    /* ignore */
  }
}

/** Removes known third-party cookies when consent is withdrawn. */
export function purgeThirdPartyCookies() {
  const host = window.location.hostname;
  const domains = ["", host, `.${host}`, `.google.com`, `.google.it`];
  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (!/^(_ga|_gid|_gat|_gcl)/.test(name)) return;
    domains.forEach((domain) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${
        domain ? `; domain=${domain}` : ""
      }`;
    });
  });
}
